'use client';

import Image from 'next/image';
import { PopupProps } from '@/types/popup';
import { useState, useEffect } from 'react';
import { getImageUrl } from '@/utils';

/**
 * 팝업 컴포넌트
 * 
 * 홈페이지에 표시되는 팝업을 렌더링하는 컴포넌트입니다.
 * 여러 팝업을 순차적으로 표시할 수 있으며, 이미지 클릭 시 링크로 이동하는 기능을 제공합니다.
 * 
 * 주요 기능:
 * - 팝업 이미지 표시 (3:4 비율 고정)
 * - 여러 팝업 간 좌우 이동
 * - 이미지 클릭 시 링크 이동 (내부/외부 링크 구분)
 * - 오늘 하루 보지 않기 기능
 * - 키보드 접근성 지원
 * - 반응형 디자인
 * - 부드러운 열기/닫기 애니메이션
 */
export default function Popup({
  isOpen,
  onClose,
  onDontShowToday,
  popups,
  currentIndex,
  onNext,
  onPrev,
}: PopupProps) {
  const [isClosing, setIsClosing] = useState(false);

  /**
   * 팝업 열기/닫기 시 body 스크롤 제어
   * 
   * 팝업이 열려있을 때 HTML 스크롤을 숨기고,
   * 팝업이 닫힐 때 또는 컴포넌트가 언마운트될 때 스크롤을 복원합니다.
   */
  useEffect(() => {
    if (isOpen) {
      // 팝업이 열릴 때 body 스크롤 숨기기
      document.body.style.overflow = 'hidden';
    } else {
      // 팝업이 닫힐 때 body 스크롤 복원
      document.body.style.overflow = 'unset';
    }

    // 컴포넌트가 언마운트될 때 body 스크롤 복원
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  /**
   * 팝업 닫기 핸들러
   * 
   * 닫기 애니메이션을 실행한 후 팝업을 완전히 닫습니다.
   */
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };

  /**
   * 오늘 하루 보지 않기 핸들러
   * 
   * 오늘 하루 보지 않기 설정을 적용하고 팝업을 닫습니다.
   */
  const handleDontShowToday = () => {
    onDontShowToday();
    handleClose();
  };

  /**
   * 다음 팝업으로 이동 핸들러
   * 
   * 마지막 팝업이 아닌 경우에만 다음 팝업으로 이동합니다.
   */
  const handleNext = () => {
    if (currentIndex < popups.length - 1) {
      onNext();
    }
  };

  /**
   * 이전 팝업으로 이동 핸들러
   * 
   * 첫 번째 팝업이 아닌 경우에만 이전 팝업으로 이동합니다.
   */
  const handlePrev = () => {
    if (currentIndex > 0) {
      onPrev();
    }
  };

  const currentPopup = popups[currentIndex];

  // 팝업 데이터가 없을 때 기본 메시지 표시
  if (!currentPopup) {
    return (
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-200 ${isClosing ? 'opacity-0' : 'opacity-100'
          }`}
      >
        {/* 반투명 오버레이 */}
        <div
          className="absolute inset-0 bg-black opacity-85"
          onClick={handleClose}
          aria-label="팝업 닫기"
        />

        {/* 기본 팝업 컨테이너 */}
        <div className="relative z-10 mx-4 bg-white rounded-lg p-8 max-w-md w-full">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">팝업</h3>
            <p className="text-gray-600 mb-6">표시할 팝업이 없습니다.</p>

            {/* 하단 버튼 영역 */}
            <div className="flex items-center justify-center space-x-3">
              {/* 오늘 하루 보지 않기 버튼 */}
              <button
                onClick={handleDontShowToday}
                className="flex items-center space-x-3 px-5 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-xl border border-gray-300 transition-colors duration-100"
                aria-label="오늘 하루 보지 않기"
              >
                <span>오늘 하루 보지 않기</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>

              {/* 닫기 버튼 */}
              <button
                onClick={handleClose}
                className="flex items-center space-x-3 px-5 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-xl border border-gray-300 transition-colors duration-100"
                aria-label="팝업 닫기"
              >
                <span>닫기</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-200 ${isClosing ? 'opacity-0' : 'opacity-100'
        }`}
    >
      {/* 반투명 오버레이 */}
      <div
        className="absolute inset-0 bg-black opacity-85"
        onClick={handleClose}
        aria-label="팝업 닫기"
      />

      {/* 팝업 컨테이너 - 3:4 비율 고정 */}
      <div className="relative z-10 mx-4 w-full [@media(min-width:360px)]:w-auto">
        {/* 이미지 팝업 - 3:4 비율 컨테이너 */}
        <div className="relative max-w-md w-full overflow-hidden mx-auto">
          {/* 3:4 비율을 위한 컨테이너 */}
          <div
            className={`relative w-full ${currentPopup.link ? 'cursor-pointer' : ''}`}
            style={{ aspectRatio: '3/4' }}
            onClick={() => {
              if (currentPopup.link) {
                if (currentPopup.link.startsWith('http')) {
                  // 외부 링크인 경우 새 탭에서 열기
                  window.open(currentPopup.link, '_blank', 'noopener,noreferrer');
                } else {
                  // 내부 링크인 경우 현재 탭에서 이동
                  window.location.href = currentPopup.link;
                }
              }
            }}
            role={currentPopup.link ? 'button' : undefined}
            tabIndex={currentPopup.link ? 0 : undefined}
            aria-label={currentPopup.link ? `${currentPopup.alt} 클릭하여 링크로 이동` : currentPopup.alt}
            onKeyDown={(e) => {
              if (currentPopup.link && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                if (currentPopup.link.startsWith('http')) {
                  window.open(currentPopup.link, '_blank', 'noopener,noreferrer');
                } else {
                  window.location.href = currentPopup.link;
                }
              }
            }}
          >
            {/* 팝업 이미지 */}
            <Image
              src={getImageUrl(currentPopup.imageUrl)}
              alt={currentPopup.alt}
              fill
              className="object-cover"
              priority
            />
          </div>
        </div>

        {/* 하단 버튼 영역 */}
        <div className="flex items-center justify-between gap-3 sm:gap-6 mt-3 sm:mt-4 w-full [@media(min-width:360px)]:w-auto text-white">
          {/* 왼쪽: 좌우 이동 버튼과 페이지네이션 */}
          <div className="flex items-center space-x-0 [@media(min-width:360px)]:space-x-1">
            {/* 이전 팝업 버튼 */}
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className={`p-1 rounded-full transition-colors text-white`}
              aria-label="이전 팝업"
            >
              <svg
                className="w-5 h-5 [@media(min-width:360px)]:w-6 [@media(min-width:360px)]:h-6 sm:w-7 sm:h-7"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>

            {/* 페이지네이션 표시 */}
            <span className="text-xs [@media(min-width:360px)]:text-base sm:text-lg font-medium w-6 [@media(min-width:360px)]:w-8 sm:w-10 text-center">
              {currentIndex + 1} / {popups.length}
            </span>

            {/* 다음 팝업 버튼 */}
            <button
              onClick={handleNext}
              disabled={currentIndex === popups.length - 1}
              className={`p-1 rounded-full transition-colors text-white`}
              aria-label="다음 팝업"
            >
              <svg
                className="w-5 h-5 [@media(min-width:360px)]:w-6 [@media(min-width:360px)]:h-6 sm:w-7 sm:h-7"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>

          {/* 오른쪽: 오늘 하루 보지 않기, 닫기 버튼 */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* 오늘 하루 보지 않기 버튼 */}
            <button
              onClick={handleDontShowToday}
              className="flex items-center space-x-1 [@media(min-width:360px)]:space-x-2 sm:space-x-3 px-2 [@media(min-width:360px)]:px-4 py-2 sm:px-5 sm:py-3 text-xs [@media(min-width:360px)]:text-sm sm:text-sm text-white hover:bg-white/10 rounded-lg sm:rounded-xl border border-white/30 transition-colors duration-100"
              aria-label="오늘 하루 보지 않기"
            >
              <span className="hidden sm:inline">오늘 하루 보지 않기</span>
              <span className="sm:hidden">오늘 안보기</span>
              <svg
                width="14"
                height="14"
                className="w-3 h-3 [@media(min-width:360px)]:w-4 [@media(min-width:360px)]:h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>

            {/* 닫기 버튼 */}
            <button
              onClick={handleClose}
              className="flex items-center space-x-1 [@media(min-width:360px)]:space-x-2 sm:space-x-3 px-2 [@media(min-width:360px)]:px-4 py-2 sm:px-5 sm:py-3 text-xs [@media(min-width:360px)]:text-sm sm:text-sm text-white hover:bg-white/10 rounded-lg sm:rounded-xl border border-white/30 transition-colors duration-100"
              aria-label="팝업 닫기"
            >
              <span>닫기</span>
              <svg
                width="14"
                height="14"
                className="w-3 h-3 [@media(min-width:360px)]:w-4 [@media(min-width:360px)]:h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 